import { CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';

/**
 * Handles smooth scrolling for in-page anchor links,
 * accounting for a sticky header and focusing the first
 * visible, focusable form field if present.
 */
export default class DarkBgCampaign {
  /**
   * Constructor
   * @param {HTMLElement|Document} element - container to bind click listener to
   */
  constructor(element = document) {
    this.element = element;
    this.headerSelector = `.${CLASS_NAMES.HEADER}.${CLASS_NAMES.STICKY}`;
    this.scrollDelay = 600;

    this.init();
  }

  /**
   * Initializes click listener for anchor scrolling
   * @returns {DarkBgCampaign} Returns the click event
   * @chainable
   */
  init() {
    this.element.addEventListener(EVENTS.CLICK, this.handleClick.bind(this));
    return this;
  }

  /**
   * Handles delegated anchor click events
   * @param {MouseEvent} event Returns the click event
   */
  handleClick(event) {
    const link = event.target.closest(SELECTORS.ANCHOR);
    if (!link) return;

    const href = link.getAttribute(SELECTORS.HREF);
    if (!href?.includes('#')) return;

    const sectionId = href.slice(href.indexOf('#'));
    if (sectionId === '#') return;

    const section = document.querySelector(sectionId);
    if (!section) return;

    event.preventDefault();

    this.scrollToSection(section);
    this.focusFormField(section);
  }

  /**
   * Scrolls to a section while accounting for sticky header offset
   * @param {HTMLElement} section Returns the scroll event
   */
  scrollToSection(section) {
    const header = document.querySelector(this.headerSelector);
    const headerHeight = header?.offsetHeight ?? 0;

    const top =
      section.getBoundingClientRect().top + window.pageYOffset - headerHeight;

    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  }

  /**
   * Focuses the first visible, focusable form field in the section (if any)
   * @param {HTMLElement} section Returns the focus event
   */
  focusFormField(section) {
    window.setTimeout(() => {
      const form = section.querySelector(SELECTORS.FORM);
      if (!form) return;

      const focusableSelectors = [
        SELECTORS.INPUT_NOT_DISABLED,
        SELECTORS.TEXT_AREA_NOT_DISABLED,
        SELECTORS.TABINDEX_NOT_DISABLED
      ].join(',');

      const fields = Array.from(form.querySelectorAll(focusableSelectors));

      const field = fields.find(DarkBgCampaign.isVisible);

      field?.focus({ preventScroll: true });
    }, this.scrollDelay);
  }

  /**
   * Determines if an element is visible in the DOM
   * @param {HTMLElement} element Current element
   * @returns {boolean} Returns the visible element
   */
  static isVisible(element) {
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      rect.width > 0 &&
      rect.height > 0
    );
  }
}
